import { DateRangePicker, type DateRangePickerProps } from '@mui/x-date-pickers-pro/DateRangePicker';
import { forwardRef } from 'react';
import { LabeledField } from '../LabeledField';

/**
 * @tier molecule
 *
 * Day-level date range picker with a stacked label. Built on
 * `@mui/x-date-pickers-pro`. Used for one-time charge date ranges. Pro
 * license required at runtime.
 *
 * Requires a `LocalizationProvider dateAdapter={AdapterDayjs}` ancestor.
 * For month-year ranges use `VyMonthYearRangePicker` instead.
 */
export type VyDateRangePickerProps = Omit<DateRangePickerProps, 'slotProps' | 'label'> & {
  /** Field label rendered above the picker. */
  label?: string;
  /** Hint shown below the input. Becomes the error message when `error` is true. */
  helperText?: string;
  /** Renders the input in the error state. */
  error?: boolean;
  /** Shows an inline clear (✕) button in the field when a range is selected. */
  clearable?: boolean;
};

export const VyDateRangePicker = forwardRef<HTMLDivElement, VyDateRangePickerProps>(
  function VyDateRangePicker({ label, helperText, error, clearable, ...rest }, ref) {
    return (
      <LabeledField label={label} helperText={helperText} error={error}>
        <DateRangePicker
          ref={ref}
          {...rest}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              error,
            },
            field: { clearable },
          }}
        />
      </LabeledField>
    );
  },
);
